import './footer.scss'

import {
	BlueskyIcon,
	FacebookIcon,
	InstagramIcon,
	LinkedInIcon,
	LocationSmallIcon,
	MailIcon,
	PhoneIcon,
	YouTubeIcon
} from 'styles/inline-icons'
import {FooterType, LinkBaseType} from 'campagne/types'
import {LinkProps, Link as UtilLink} from 'util/link'
import {Mod, style} from 'util/campagne/style'
import React, {Fragment, FunctionComponent, ReactElement} from 'react'
import {ShareContainer, ShareLink} from './share'

import {ButtonPrimary} from 'views/ui/Button'
import {FooterEfLogo} from '../../layout/footer/FooterEfLogo'
import {ReactComponent as LogoNl} from '../../../assets/images/logo_nl.svg'
import {Theme} from 'views/campagne/theme'
import {useTranslation} from 'util/i18n'

const styles = style('footer', ['contact', 'contactitem', 'links', 'linkitem'])

export type FooterContactType = {
	name: string
	address: string
	city: string
	phone: string
	mail: string
}

export const Footer: FunctionComponent<FooterType> = ({...props}) => (
	<footer class={styles}>
		<Theme.Container>
			<div class={styles('line')}>
				<Theme.HR />
			</div>
		</Theme.Container>
		<FooterTop {...props} />
		<FooterBottom {...props} />
	</footer>
)

export const FooterContact: FunctionComponent<FooterContactType> = ({
	name,
	address,
	city,
	phone,
	mail
}) => {
	return (
		<div class={styles.contact}>
			<UtilLink to={'/nl'} class={styles.contact('logo')}>
				<LogoNl />
			</UtilLink>
			{name && address && city && (
				<FooterContactItem icon={<LocationSmallIcon />}>
					<Theme.P mod="small">{name}</Theme.P>
					<Theme.P mod="small">{address}</Theme.P>
					<Theme.P mod="small">{city}</Theme.P>
				</FooterContactItem>
			)}
			{phone && (
				<FooterContactItem icon={<PhoneIcon />}>
					<UtilLink to={`tel:${phone}`}>
						<Theme.P mod="small">{phone}</Theme.P>
					</UtilLink>
				</FooterContactItem>
			)}
			{mail && (
				<FooterContactItem
					icon={
						<MailIcon
							className={styles.contactitem('icon').mod('mail').toString()}
						/>
					}
				>
					<Theme.Link
						className={styles.contactitem('link').mod('mail').toString()}
						to={`mailto:${mail}`}
					>
						{mail}
					</Theme.Link>
				</FooterContactItem>
			)}
		</div>
	)
}

export const FooterTop: FunctionComponent<FooterType> = ({
	left,
	right,
	primary,
	secondary,
	...props
}) => {
	return (
		<div class={styles('top')}>
			<Theme.Container>
				<div class={styles('top-linkscontainer')}>
					<FooterContact {...props} />
					<FooterLinks>
						{left &&
							left.map((link) => (
								<FooterLinkItem mod="left" to={link.url}>
									{link.description}
								</FooterLinkItem>
							))}
					</FooterLinks>
					<FooterLinks>
						{right &&
							right.map((link) => (
								<FooterLinkItem to={link.url}>
									{link.description}
								</FooterLinkItem>
							))}
					</FooterLinks>
					<div class={styles('top-buttonlinks').with(styles.links)}>
						<ButtonPrimary
							to={primary.url}
							type={primary.type}
							target={primary.target}
							id="gift-footer"
						>
							{primary.description}
						</ButtonPrimary>
						<Theme.Button
							to={secondary.url}
							target={secondary.target}
							type={secondary.type}
							mod={{boldtext: true, outline: true, whitebg: true}}
							id="register-footer"
						>
							<Theme.ButtonText>{secondary.description}</Theme.ButtonText>
						</Theme.Button>
					</div>
				</div>
				<div class={styles('top-social')}>
					<FooterEfLogo />
					<Social />
				</div>
			</Theme.Container>
		</div>
	)
}

const Social: React.FC<{}> = () => {
	return (
		<ShareContainer>
			<ShareLink
				href="https://www.facebook.com/broederlijkdelen"
				icon={
					<FacebookIcon
						style={{
							width: 'auto',
							height: '1.5rem',
							maxHeight: '31px'
						}}
					/>
				}
			/>
			<ShareLink
				href="https://www.instagram.com/broederlijk.delen/?hl=nl"
				icon={
					<InstagramIcon
						style={{
							width: 'auto',
							height: '1.5rem',
							maxHeight: '31px'
						}}
						ariaLabel="instagram"
					/>
				}
			/>
			<ShareLink
				href="https://bsky.app/profile/broederlijkdelen.bsky.social"
				icon={
					<BlueskyIcon
						style={{
							width: 'auto',
							height: '1.5rem',
							maxHeight: '31px'
						}}
						ariaLabel="bluesky"
					/>
				}
			/>
			<ShareLink
				href="https://www.youtube.com/user/BroederlijkDelen"
				icon={
					<YouTubeIcon
						style={{
							width: 'auto',
							height: '1.5rem',
							maxHeight: '31px'
						}}
						ariaLabel="YouTube"
					/>
				}
			/>
			<ShareLink
				href="https://www.linkedin.com/company/broederlijk-delen-vzw"
				icon={
					<LinkedInIcon
						style={{
							width: 'auto',
							height: '1.5rem',
							maxHeight: '31px'
						}}
						ariaLabel="LinkedIn"
					/>
				}
			/>
		</ShareContainer>
	)
}

export const FooterBottom: FunctionComponent<{
	bottom?: Array<LinkBaseType>
}> = ({bottom}) => {
	const t = useTranslation('campagne').footer
	return (
		<div class={styles('bottom')}>
			<Theme.Container>
				<div class={styles('bottom-container')}>
					<p class={styles('bottom-copyright')}>&copy; {t.copyright}</p>
					{bottom && bottom.length > 0 && (
						<div class={styles('bottom-links')}>
							{bottom.map(
								(link, i) =>
									link && (
										<Fragment>
											{i > 0 && (
												<span className={styles('bottom-links-divider')}>
													|
												</span>
											)}
											<UtilLink
												to={link.url}
												className={styles('bottom-links-link')}
											>
												{link.description || link.title}
											</UtilLink>
										</Fragment>
									)
							)}
						</div>
					)}
				</div>
			</Theme.Container>
		</div>
	)
}

export const FooterContactItem: FunctionComponent<{
	icon: ReactElement
}> = ({icon, children}) => (
	<div class={styles.contactitem}>
		<span class={styles.contactitem('icon')}>{icon}</span>
		<div class={styles.contactitem('children')}>{children}</div>
	</div>
)

export const FooterLinkItem: FunctionComponent<
	LinkProps & {mod?: Mod<'left'>}
> = ({children, mod, ...props}) => (
	<span class={styles.linkitem.mod(mod)}>
		<UtilLink {...props}>{children}</UtilLink>
	</span>
)

export const FooterLinks: FunctionComponent = ({children}) => (
	<div class={styles.links}>{children}</div>
)
